import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { posApi, saasApi, authApi, storefrontAdminApi } from '@/services/api';
import { Tags, Loader2, AlertCircle, X, Upload, Store } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import CategoryIcon from '@/components/pos/CategoryIcon';
import IconPicker from '@/components/pos/IconPicker';
import ColorPicker from '@/components/pos/ColorPicker';
import { getDefaultCategoryColor } from '@/constants/posCategoryIcons';
import { resizeImage } from '@/utils/imageUpload';
import { resolveFileUrl } from '@/utils/fileUrl';
import type { POSCategory } from '@/types';
import ConfirmModal from '@/components/common/ConfirmModal';

interface Props {
  editing?: POSCategory | null;
  onClose: () => void;
  /** Called after a successful create/update with the category name. */
  onSaved?: (categoryName: string) => void;
}

/** Shared add/edit category modal (name, type, order, image, color, icon with industry filter). */
export default function CategoryFormModal({ editing, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  useEscapeToClose(onClose, true);

  const [form, setForm] = useState({
    category_name: editing?.category_name || '',
    category_type: editing?.category_type || 'Product',
    display_order: editing?.display_order || 0,
    pos_category_icon: editing?.pos_category_icon || '',
    pos_category_image: editing?.pos_category_image || '',
    pos_category_color: editing?.pos_category_color || '',
    storefront_banner: editing?.storefront_banner || '',
    storefront_banner_title: editing?.storefront_banner_title || '',
    show_on_storefront: editing?.show_on_storefront ? 1 : 0,
  });
  const [uploadingBanner, setUploadingBanner] = useState(false);

  // Store industry drives the IconPicker recommendations (cached query shared with pages).
  const { data: companyProfile } = useQuery({
    queryKey: ['companyProfile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message as { business_type?: string };
    },
    staleTime: 60_000,
  });

  const { data: publicStoreEnabled } = useQuery({
    queryKey: ['storefrontEnabledForCategories'],
    queryFn: async () => {
      try {
        const res = await storefrontAdminApi.getSettings();
        return Boolean(res.data.message?.enable_storefront);
      } catch {
        return false;
      }
    },
    staleTime: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['categoriesList'] });
    qc.invalidateQueries({ queryKey: ['posCategories'] });
    qc.invalidateQueries({ queryKey: ['categories'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: unknown) => posApi.createCategory(data),
    onSuccess: (res) => {
      invalidate();
      onSaved?.((res?.data?.message?.name as string) || form.category_name);
      onClose();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || t('categories.failedCreate'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: unknown) => posApi.updateCategory(data),
    onSuccess: () => {
      invalidate();
      onSaved?.(editing?.name || form.category_name);
      onClose();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || t('categories.failedUpdate'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => posApi.deleteCategory(editing!.name),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || t('common.error'));
    },
  });

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowConfirm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.category_name.trim()) {
      setErrorMsg(t('categories.nameRequired'));
      return;
    }

    const payload = {
      ...form,
      pos_category_color: form.pos_category_color || getDefaultCategoryColor(form.category_name),
    };

    if (editing) {
      updateMutation.mutate({ ...payload, name: editing.name });
    } else {
      createMutation.mutate(payload);
    }
  };

  const uploadPublicImage = async (file: File, maxW: number, maxH: number) => {
    const resized = await resizeImage(file, maxW, maxH);
    const uploadFile = new File([resized], file.name, { type: 'image/jpeg' });
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('is_private', '0');
    const res = await saasApi.uploadFile(formData);
    return res.data.message.file_url as string;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPublicImage(file, 400, 400);
      setForm((prev) => ({ ...prev, pos_category_image: url }));
    } catch (err) {
      setErrorMsg((err as Error)?.message || t('categories.imageUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      // Wide banner for category pages on the public store
      const url = await uploadPublicImage(file, 1600, 600);
      setForm((prev) => ({ ...prev, storefront_banner: url }));
    } catch (err) {
      setErrorMsg((err as Error)?.message || t('categories.bannerUploadFailed'));
    } finally {
      setUploadingBanner(false);
    }
  };

  const clearImage = () => setForm((prev) => ({ ...prev, pos_category_image: '' }));
  const clearBanner = () => setForm((prev) => ({ ...prev, storefront_banner: '' }));

  const effectiveColor = form.pos_category_color || getDefaultCategoryColor(form.category_name || 'Category');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Tags className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {editing ? t('categories.edit') : t('categories.add')}
            </h3>
            <p className="text-sm text-gray-500">
              {editing ? t('categories.editSubtitle') : t('categories.addSubtitle')}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-y-auto flex-1 min-h-0">
          {errorMsg && (
            <div className="lg:col-span-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{errorMsg}</p>
            </div>
          )}

          <div className="space-y-4">
          <div className="flex items-center gap-3">
            <CategoryIcon
              icon={form.pos_category_icon}
              image={form.pos_category_image}
              color={effectiveColor}
              size="lg"
            />
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.name')} *</label>
              <input
                type="text"
                value={form.category_name}
                onChange={(e) => setForm({ ...form, category_name: e.target.value })}
                className="input-field w-full"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.type')} *</label>
              <select
                value={form.category_type}
                onChange={(e) => setForm({ ...form, category_type: e.target.value as 'Product' | 'Service' })}
                className="input-field w-full"
              >
                <option value="Product">{t('categories.product')}</option>
                <option value="Service">{t('categories.service')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.displayOrder')}</label>
              <input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                className="input-field w-full"
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('categories.posAppearance')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.image')}</label>
              <div className="flex items-center gap-3">
                {form.pos_category_image ? (
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <img src={resolveFileUrl(form.pos_category_image)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-black/50 text-white rounded-full hover:bg-black/70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors bg-white">
                    <Upload className="w-4 h-4" />
                    {uploading ? t('common.uploading') : t('categories.uploadImage')}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                )}
                <p className="text-xs text-gray-500">{t('categories.imageHint')}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.color')}</label>
              <ColorPicker
                value={form.pos_category_color}
                onChange={(color) => setForm((prev) => ({ ...prev, pos_category_color: color }))}
              />
              <p className="text-xs text-gray-500 mt-1">{t('categories.colorHint')}</p>
            </div>
          </div>

          {publicStoreEnabled ? (
            <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-teal-800 flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5" />
                {t('categories.storeAppearance')}
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={!!form.show_on_storefront}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, show_on_storefront: e.target.checked ? 1 : 0 }))
                  }
                />
                {t('categories.showOnStore')}
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('categories.storeBanner')}
                </label>
                <div className="flex items-start gap-3 flex-wrap">
                  {form.storefront_banner ? (
                    <div className="relative w-full max-w-xs aspect-[3/1] rounded-lg overflow-hidden border border-teal-200 bg-white">
                      <img
                        src={resolveFileUrl(form.storefront_banner)}
                        alt=""
                        className="w-full h-full object-cover object-center"
                      />
                      <button
                        type="button"
                        onClick={clearBanner}
                        className="absolute top-1 right-1 p-0.5 bg-black/50 text-white rounded-full hover:bg-black/70"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-2 border border-teal-200 rounded-lg text-sm text-teal-900 hover:bg-teal-50 cursor-pointer transition-colors bg-white">
                      <Upload className="w-4 h-4" />
                      {uploadingBanner ? t('common.uploading') : t('categories.uploadBanner')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerUpload}
                        disabled={uploadingBanner}
                        className="hidden"
                      />
                    </label>
                  )}
                  <p className="text-xs text-teal-800/80">{t('categories.bannerHint')}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('categories.bannerTitle')}
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={form.storefront_banner_title}
                  onChange={(e) => setForm({ ...form, storefront_banner_title: e.target.value })}
                  placeholder={t('categories.bannerTitlePlaceholder')}
                />
              </div>
            </div>
          ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.icon')}</label>
            <p className="text-xs text-gray-500 mb-2">{t('categories.iconHint')}</p>
            <IconPicker
              value={form.pos_category_icon}
              onChange={(name) => setForm((prev) => ({ ...prev, pos_category_icon: name }))}
              industry={companyProfile?.business_type}
            />
          </div>
          </div>

          <div className="px-5 py-4 border-t border-gray-100 flex gap-3 shrink-0 bg-white">
            {editing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.delete')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                uploading ||
                uploadingBanner ||
                deleteMutation.isPending
              }
              className="flex-1 btn-primary flex justify-center items-center gap-2 py-2.5"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {editing ? t('common.update') : t('common.create')}
            </button>
          </div>
        </form>
      </div>

      {showConfirm && (
        <ConfirmModal
          title={t('common.delete') || 'Delete'}
          message={t('common.confirmDelete') || 'Are you sure you want to delete this?'}
          onConfirm={confirmDelete}
          onClose={() => setShowConfirm(false)}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
